// One notifications implementation, shared by the bell and Workspace → Notifications.
//
// It lived inside NotificationBell. The Inbox needed the same rows, and the
// alternative was a second copy of the query, the realtime subscription and the
// read/dismiss semantics — which is exactly how the two would have drifted.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';

export type Notif = {
  id: string;
  kind: string;
  body: string;
  link: string | null;
  read_at: string | null;
  created_at: string;
};

/** What a write resolves to: the error to show, or null once it landed. */
type WriteError = string | null;

export function useNotifications(uid: string | undefined, limit = 20) {
  const [items, setItems] = useState<Notif[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  // A failed read is not an empty inbox (WS-V1): `data ?? []` told people they
  // had no notifications when the request had failed. The last good list and
  // count stay; the surface shows this and a Retry instead of "nothing here".
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !uid) {
      setLoading(false);
      return;
    }
    const sb = supabase;
    const list = await sb
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (!list.error) setItems((list.data as Notif[]) ?? []);

    // Counted server-side, NOT as items.filter(!read_at).length — that counted
    // unread within the page window, so the bell (20 rows) and the Inbox (200)
    // showed one user two different unread numbers for the same inbox.
    const head = await sb
      .from('notifications')
      .select('id', { head: true, count: 'exact' })
      .is('read_at', null);
    if (!head.error) setUnread(head.count ?? 0);
    setError((list.error ?? head.error)?.message ?? null);
    // After the count, so "loaded" means the unread number is real too.
    setLoading(false);
  }, [uid, limit]);

  useEffect(() => {
    void load();
    if (!supabase || !uid) return;
    const sb = supabase;
    const ch = sb
      .channel(`notif:${uid}:${limit}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `profile_id=eq.${uid}` },
        () => void load(),
      )
      .subscribe();
    // Without this the list fetches once per page load and realtime is the ONLY
    // delivery path — one dropped socket and nothing ever arrives again. A poll
    // would hold a connection open all day for the same result.
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      void sb.removeChannel(ch);
    };
  }, [uid, limit, load]);

  // Every write below resolves to its error (or null) and then reloads: a
  // failure used to vanish, and the optimistic change with it, with nothing said.

  // Opening one notification reads that one (WS-06). Before this the only ways
  // to read anything were "Mark all read" and opening the bell, which read all
  // twenty without one being opened. Optimistic; load() recounts `unread`.
  const markRead = useCallback(
    async (id: string): Promise<WriteError> => {
      if (!supabase) return null;
      const at = new Date().toISOString();
      setItems((rs) => rs.map((r) => (r.id === id && !r.read_at ? { ...r, read_at: at } : r)));
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: at })
        .eq('id', id)
        .is('read_at', null);
      void load();
      return error?.message ?? null;
    },
    [load],
  );

  const markAllRead = useCallback(async (): Promise<WriteError> => {
    if (!supabase || !uid) return null;
    // Scoped to the rows actually rendered. An unfiltered `.is('read_at', null)`
    // also cleared everything past the limit — notifications the user had never
    // seen were marked read by opening the panel once.
    const ids = items.filter((n) => !n.read_at).map((n) => n.id);
    if (!ids.length) return null;
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .in('id', ids);
    void load();
    return error?.message ?? null;
  }, [uid, items, load]);

  // Optimistic, then reconciled either way: RLS scopes the delete to your own
  // rows, so a failure means the row is still there and load() puts it back; a
  // success may have removed unread rows, and load() is what recounts `unread`
  // (realtime doesn't deliver filtered DELETEs).
  const dismiss = useCallback(
    async (id: string): Promise<WriteError> => {
      if (!supabase) return null;
      setItems((rs) => rs.filter((r) => r.id !== id));
      const { error } = await supabase.from('notifications').delete().eq('id', id);
      void load();
      return error?.message ?? null;
    },
    [load],
  );

  // Read rows only. This was "Clear all": a DELETE of every loaded row, unread
  // included (up to 200 on the tab), one mis-click from "Mark all read", with no
  // confirm and no undo. Scoped to read_at in the query as well as the id list,
  // so a stale local list still can't take an unread row with it. It now removes
  // only what you have already seen, so it needs no confirm.
  const clearRead = useCallback(async (): Promise<WriteError> => {
    if (!supabase) return null;
    const ids = items.filter((n) => n.read_at).map((n) => n.id);
    if (!ids.length) return null;
    setItems((rs) => rs.filter((r) => !ids.includes(r.id)));
    const { error } = await supabase
      .from('notifications')
      .delete()
      .in('id', ids)
      .not('read_at', 'is', null);
    void load();
    return error?.message ?? null;
  }, [items, load]);

  return {
    items,
    loading,
    error,
    unread,
    markRead,
    markAllRead,
    dismiss,
    clearRead,
    reload: load,
  };
}
